
export default function StatsCards() {
  const athleticStats = [
    { label: 'Digs Per Set', value: '5.8', icon: 'ri-shield-fill', color: 'green' },
    { label: 'Passing %', value: '2.4', icon: 'ri-check-fill', color: 'blue' },
    { label: 'Serve Receive %', value: '92%', icon: 'ri-percent-fill', color: 'purple' },
    { label: 'Aces Per Set', value: '1.5', icon: 'ri-flashlight-fill', color: 'yellow' },
    { label: 'Serve %', value: '95%', icon: 'ri-check-double-fill', color: 'orange' },
    { label: 'Defensive Saves', value: '4.2', icon: 'ri-hand-heart-fill', color: 'red' }
  ];

  const academicAchievements = [
    { title: 'Honor Roll', description: 'Multiple Semesters', icon: 'ri-medal-fill', color: 'yellow' },
    { title: 'AP Courses', description: 'Advanced Classes', icon: 'ri-book-open-fill', color: 'blue' },
    { title: 'Student Athlete', description: 'Academic Excellence', icon: 'ri-trophy-fill', color: 'orange' },
    { title: 'GPA', description: '3.4 Unweighted', icon: 'ri-star-fill', color: 'green' }
  ];

  const awards = [
    { title: 'All-Conference Selection', year: '2024', icon: 'ri-award-fill' },
    { title: 'Team MVP', year: '2024', icon: 'ri-trophy-fill' },
    { title: 'Tournament All-Star', year: '2023-2024', icon: 'ri-star-fill' },
    { title: 'Best Defensive Player', year: '2024', icon: 'ri-shield-fill' },
    { title: 'Leadership Award', year: '2023', icon: 'ri-team-fill' },
    { title: 'Most Improved', year: '2023', icon: 'ri-line-chart-fill' }
  ];

  const favoriteMemories = [
    {
      title: '1st Dig',
      description: 'My first defensive save in a varsity match - the moment I knew I belonged at this level',
      icon: 'ri-heart-fill',
      color: 'red'
    },
    {
      title: 'Championship Win',
      description: 'Leading my club team to the regional championship with exceptional defensive performance',
      icon: 'ri-trophy-fill',
      color: 'yellow'
    },
    {
      title: 'Perfect Game',
      description: 'Going 18/18 on serves in a crucial tournament match',
      icon: 'ri-flashlight-fill',
      color: 'orange'
    },
    {
      title: 'Team Captain',
      description: 'Being voted team captain by my teammates - the ultimate honor',
      icon: 'ri-team-fill',
      color: 'purple'
    }
  ];

  const colorClasses: Record<string, { bg: string; text: string; ring: string }> = {
    orange: { bg: 'bg-orange-500', text: 'text-orange-500', ring: 'ring-orange-500' },
    red: { bg: 'bg-red-500', text: 'text-red-500', ring: 'ring-red-500' },
    blue: { bg: 'bg-blue-500', text: 'text-blue-500', ring: 'ring-blue-500' },
    green: { bg: 'bg-green-500', text: 'text-green-500', ring: 'ring-green-500' },
    purple: { bg: 'bg-purple-500', text: 'text-purple-500', ring: 'ring-purple-500' },
    yellow: { bg: 'bg-yellow-500', text: 'text-yellow-500', ring: 'ring-yellow-500' }
  };

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-orange-50">
      <div className="max-w-7xl mx-auto">
        {/* Athletic Stats */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
              ATHLETIC STATS
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-orange-500 to-red-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {athleticStats.map((stat, index) => {
              const colors = colorClasses[stat.color];
              return (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center`}>
                      <i className={`${stat.icon} text-white text-2xl`}></i>
                    </div>
                    <span className={`text-4xl font-black ${colors.text}`}>{stat.value}</span>
                  </div>
                  <p className="text-gray-600 font-semibold text-lg">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Academic Achievements */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
              ACADEMIC EXCELLENCE
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-blue-500 to-green-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {academicAchievements.map((achievement, index) => {
              const colors = colorClasses[achievement.color];
              return (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  <div className={`w-16 h-16 ${colors.bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <i className={`${achievement.icon} text-white text-3xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{achievement.title}</h3>
                  <p className="text-gray-600">{achievement.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Awards */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
              AWARDS &amp; HONORS
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-yellow-500 to-orange-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {awards.map((award, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-yellow-500"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${award.icon} text-white text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{award.title}</h3>
                    <p className="text-gray-600">{award.year}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Favorite Moments */}
        <div>
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
              FAVORITE MOMENTS
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {favoriteMemories.map((memory, index) => {
              const colors = colorClasses[memory.color];
              return (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mb-4`}>
                    <i className={`${memory.icon} text-white text-3xl`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{memory.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{memory.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
